import { useRef, useState } from 'react';
import { getPosition, haptic, isNative, pickFromGallery, takePhoto } from '../lib/native';
import { Camera, Hourglass, Image as ImageIcon, X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';


export interface UploadedPhoto {
  /** 压缩+水印后的 Blob。离线时先存本地,同步时才传对象存储 */
  blob: Blob;
  previewUrl: string;
}

/**
 * 拍照上传。
 *
 * 关键处理:
 *   1. **客户端压缩**到长边 1280px —— 原图 4MB 在司机流量下上传经常失败
 *   2. **打水印**(时间 + GPS)—— 争议场景下没水印的照片基本没证明力
 *   3. **直传对象存储**,不经过 API 进程
 * 见 docs/05-driver-app.md §05.8
 */
export function PhotoPicker({
  purpose,
  max,
  photos,
  onChange,
}: {
  purpose: string;
  max: number;
  photos: UploadedPhoto[];
  onChange: (p: UploadedPhoto[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  /** 相册那条路:同一个 input,但不带 capture —— 带了就直接开相机 */
  const galleryRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 点「拍照」。
   *
   * ★ 打包成 App 之后走**原生相机**,浏览器里才回落到 `<input capture>`。
   *
   *   之前这里只有 input:装成 App 也还是走 WebView 的文件选择器 ——
   *   多一步「拍照/相册/文件」的选择弹窗,而且在部分安卓机型上
   *   `capture` 会被忽略、直接打开相册。司机戴着手套、单手拿着包裹,
   *   每多一步都是实打实的时间。
   *
   *   `native.ts` 这层抽象一直在,只是这个最该用它的地方没接上。
   */
  async function shoot(source: 'CAMERA' | 'GALLERY' = 'CAMERA') {
    if (photos.length >= max || busy) return;

    if (!isNative) {
      // Web:两个 input,区别只在带不带 capture
      (source === 'CAMERA' ? inputRef : galleryRef).current?.click();
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const raw = source === 'CAMERA' ? await takePhoto() : await pickFromGallery();
      if (!raw) return; // 用户取消
      await addPhoto(raw);
      void haptic('light');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addPhoto(source: Blob): Promise<void> {
    // ★ 只做压缩和水印,**不上传** —— 上传交给 outbox 队列。
    //   司机在没信号的仓库里拍完照就能走,不用站在原地等网络
    const blob = await compressAndStamp(source);
    onChange([...photos, { blob, previewUrl: URL.createObjectURL(blob) }]);
  }

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || photos.length >= max) return;

    setBusy(true);
    setError(null);
    try {
      await addPhoto(file);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={p.previewUrl} className="relative">
            <img
              src={p.previewUrl}
              alt=""
              className="w-20 h-20 rounded-xl object-cover border border-slate-200 dark:border-slate-700"
            />
            <button
              onClick={() => onChange(photos.filter((_, j) => j !== i))}
              className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-slate-900/80
                         text-white text-xs grid place-items-center"
              aria-label="删除"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}

        {/*
          ★ 光一个 📷 图标不够。
            司机被「需要拍照后才能提交」拦住的时候,他要找的是「在哪拍」——
            一个没有文字的图标按钮不会被认成那个入口(读屏也只会念「按钮」)。
            所以图标下面写清楚,并给可访问名字。
        */}
        {photos.length < max && (
          <button
            onClick={() => void shoot()}
            disabled={busy}
            aria-label={busy ? '正在处理照片' : '拍照'}
            className="grid h-20 w-20 place-items-center gap-0.5 rounded-xl border-2 border-dashed
                       border-slate-300 text-slate-500 disabled:opacity-50
                       dark:border-slate-600 dark:text-slate-400"
          >
            <InlineIcon icon={busy ? Hourglass : Camera} className="text-2xl" />
            <span className="text-xs">{busy ? '处理中' : '拍照'}</span>
          </button>
        )}

        {/*
          ★ 相册这条路也要给。
            司机在门口先拍了照、手机进口袋,点确认时才发现没存上;
            或者货是同事拍的、发在群里的。只给「拍照」一条路的话,
            他就卡在那儿 —— 而卡住的后果是这一单迟迟确认不了。
        */}
        {photos.length < max && (
          <button
            onClick={() => void shoot('GALLERY')}
            disabled={busy}
            aria-label="从相册选"
            className="grid h-20 w-20 place-items-center gap-0.5 rounded-xl border-2 border-dashed
                       border-slate-300 text-slate-500 disabled:opacity-50
                       dark:border-slate-600 dark:text-slate-400"
          >
            <InlineIcon icon={ImageIcon} className="text-2xl" />
            <span className="text-xs">相册</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        // capture:直接开相机而不是相册,减少一步操作
        capture="environment"
        onChange={pick}
        className="hidden"
      />
      {/* ★ 不带 capture 的那一个 —— 浏览器会打开相册/文件选择 */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        onChange={pick}
        className="hidden"
      />

      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

/** 压完之后的目标上限。超了就再降一档质量重压 */
const TARGET_BYTES = 250 * 1024;

/**
 * 压缩 + 打水印。
 * 原图 4MB → 约 200KB,司机流量吃得消,上传也不容易失败。
 */
async function compressAndStamp(file: Blob): Promise<Blob> {
  /*
   * ★ 解不开就原样返回,别把司机卡在这儿。
   *
   *   iPhone 相册里的 HEIC、某些机型的深度图,`createImageBitmap` 会直接抛。
   *   抛出去的话他点「相册」什么都不会发生 —— 而他手上还提着货。
   *   原图更费流量,但**能传上去**比省流量重要。
   */
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const MAX = 1280;
  const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('无法处理图片');
  ctx.drawImage(bitmap, 0, 0, w, h);

  // 水印:时间 + 坐标。没有水印的照片在争议时基本没有证明力 ——
  // 谁都可以事后补拍一张
  const geo = await quickPosition();
  const lines = [
    new Date().toLocaleString('zh-CN'),
    geo ? `${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)}` : '定位不可用',
  ];

  const fontSize = Math.max(14, Math.round(w / 40));
  ctx.font = `${fontSize}px sans-serif`;
  ctx.textBaseline = 'bottom';

  const pad = fontSize * 0.5;
  const boxH = lines.length * fontSize * 1.35 + pad;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(0, h - boxH, w, boxH);

  ctx.fillStyle = '#fff';
  lines.forEach((line, i) => {
    ctx.fillText(line, pad, h - pad - (lines.length - 1 - i) * fontSize * 1.35);
  });

  /*
   * ★ 一刀切的质量不够 —— 纹理复杂的图(货架、印花包装)在 1280 下
   *   照样能到 500KB 以上,而司机是在路上用手机流量传。
   *   压完看一眼大小,超了就再降一档,最多降三次。
   *   下限守在 0.5:再低门牌号就糊了,而看清门牌正是这张照片的目的。
   */
  const encode = (q: number) =>
    new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('压缩失败'))),
        'image/jpeg',
        q,
      );
    });

  let out = await encode(0.75);
  for (const q of [0.65, 0.55, 0.5]) {
    if (out.size <= TARGET_BYTES) break;
    out = await encode(q);
  }
  return out;
}

/**
 * 水印用的定位。
 *
 * ★ 走 `native.ts`,不直接调 `navigator.geolocation`。
 *   在 WebView 里浏览器那套定位的权限模型和原生插件不一样,
 *   有的机型上直接拿不到 —— 结果是水印上没有 GPS,
 *   而水印的全部意义就是「这张照片是在那个地址拍的」。
 *
 * 3 秒拿不到就算了:定位失败不该拦着司机交单,
 * 围栏校验在后端另有一套。
 */
async function quickPosition(): Promise<{ lat: number; lng: number } | null> {
  const p = await getPosition(3000);
  return p ? { lat: p.lat, lng: p.lng } : null;
}
